<template>
  <Modal
    buttons-class="q-pa-md"
    content-class="row full-height"
    full-height
    :loading="loading"
    :modal-style="modalStyle"
    :offset-menu="offsetMenu"
    :title="title"
  >
    <iframe
      v-if="!loading"
      allowtransparency="true"
      class="scroll full-width full-height no-border"
      :src="playerUrl"
    />

    <template #buttons>
      <div v-if="showTooltip">
        <QIcon
          color="primary"
          name="info"
          size="xs"
        />
        <span v-if="!mediaRequest">
          {{ $t("modals.tveyes_player_modal.archive_text") }}
        </span>
        <span v-if="tvEyesMediaRequestStatus === 'In Progress'">
          {{ $t("modals.tveyes_player_modal.in_progress_text") }}
        </span>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import { type CSSProperties, computed, onMounted, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import Modal from "shared/components/core/modals/Modal.vue";
import {
  fetchTveyesMentionMediaRequest,
  isTvEyesBroadcastMention,
} from "shared/services/mention";
import { type Nullable } from "shared/types";
import type {
  MediaRequest,
  PodcastEpisode,
  RadioClip,
  TvCaption,
} from "shared/types/mentions";
import {
  isPodcastEpisode,
  isRadioClip,
  isTvCaption,
} from "shared/types/mentions/guards";

const props = defineProps<{
  mention: TvCaption | RadioClip | PodcastEpisode;
  forceFullBroadcast: boolean;
  mediaRequest?: MediaRequest;
}>();

const loading = ref<boolean>(false);
const mediaRequest = ref<Nullable<MediaRequest>>(props.mediaRequest || null);
const mentionData = ref<Nullable<TvCaption | RadioClip | PodcastEpisode>>(null);

const offsetMenu = useMediaQuery("(min-width: 1165px)");
const fullWidthModal = useMediaQuery("(max-width: 1370px)");

const modalStyle = computed<CSSProperties>(() => {
  const styles: CSSProperties = {
    maxWidth: fullWidthModal.value ? "100%" : "80%",
  };

  return styles;
});

const title = computed<string>(() =>
  isTvCaption(props.mention)
    ? props.mention.program?.name || props.mention.source?.name || ""
    : props.mention.source?.name || ""
);

const tvEyesMediaRequestStatus = computed<Nullable<string>>(
  () => mediaRequest.value?.status || null
);

const showTooltip = computed<boolean>(
  () =>
    !loading.value &&
    (!mediaRequest.value || tvEyesMediaRequestStatus.value === "In Progress")
);

const playerUrl = computed<string>(() => {
  if (
    tvEyesMediaRequestStatus.value === "Completed" &&
    !props.forceFullBroadcast
  ) {
    return mediaRequest.value!.media_url.replace("http://", "https://");
  }

  return (
    mentionData.value?.tveyes_player_url || props.mention.tveyes_player_url!
  );
});

async function fetchMentionData() {
  let prefix = "";

  if (isTvCaption(props.mention)) {
    prefix = "TC";
  } else if (isRadioClip(props.mention)) {
    prefix = "RA";
  } else if (isPodcastEpisode(props.mention)) {
    prefix = "PE";
  }

  const mentionId = `${prefix}${props.mention.id}`;

  try {
    const response = await $streemApiV1.get(`mentions/${mentionId}`);
    mentionData.value = response.data;
  } catch (error) {
    ErrorDialog(getLocaleText("modals.tveyes_player_modal.load_error"), error);
  }
}

async function loadMediaRequest() {
  if (!isTvEyesBroadcastMention(props.mention)) return;

  try {
    mediaRequest.value = await fetchTveyesMentionMediaRequest(props.mention);
  } catch (error) {
    ErrorDialog(getLocaleText("modals.tveyes_player_modal.load_error"), error);
  }
}

onMounted(async () => {
  loading.value = true;

  try {
    await Promise.all([
      fetchMentionData(),
      !mediaRequest.value ? loadMediaRequest() : Promise.resolve(),
    ]);
  } finally {
    loading.value = false;
  }
});
</script>
